// Files attached to a Copilot message: uploaded through the platform's own
// file flow as soon as they are picked, sent with the message by id. Shared
// by the full Copilot page and the side panel.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Image, notification, Tooltip } from "antd";
import {
  PaperClipOutlined, LoadingOutlined, FileImageOutlined, VideoCameraOutlined, FilePdfOutlined,
  FileWordOutlined, FileExcelOutlined, FileTextOutlined, EyeOutlined,
} from "@ant-design/icons";
import { uploadSingleFile, generateDownloadUrl } from "../../services/fileUploadService";
import { ContextRow, ContextChip, MicButton } from "../../Components/Copilot/CopilotPanelStyle";
import { FIG_INK, FIG_MUTED, HAIRLINE, PRIMARY } from "../../design";

export const MAX_FILES = 5;
export const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.mp4,.pdf,.docx,.xlsx,.xls,.csv,.txt,.json";
const KIND = {
  "image/png": "image", "image/jpeg": "image", "image/jpg": "image", "image/webp": "image", "image/gif": "image",
  "video/mp4": "video", "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "sheet", "application/vnd.ms-excel": "sheet", "text/csv": "sheet",
  "text/plain": "text", "application/json": "text",
};
// Some browsers leave the type empty for csv/txt: name it from the extension.
const BY_EXT = { csv: "text/csv", txt: "text/plain", json: "application/json", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" };
const ICON = { image: FileImageOutlined, video: VideoCameraOutlined, pdf: FilePdfOutlined, document: FileWordOutlined, sheet: FileExcelOutlined, text: FileTextOutlined };

const typed = (file) => {
  if (file.type) return file;
  const ext = String(file.name.split(".").pop() || "").toLowerCase();
  return BY_EXT[ext] ? new File([file], file.name, { type: BY_EXT[ext] }) : file;
};

/** The files waiting in the message box: add, remove, and what to send. */
export function useAttachments() {
  const [files, setFiles] = useState([]);
  const seq = useRef(0);
  const count = useRef(0);
  count.current = files.length;
  const add = useCallback((list) => {
    const picked = Array.from(list || []).map(typed);
    const room = Math.max(MAX_FILES - count.current, 0);
    if (picked.length > room) notification.warning({ message: `Up to ${MAX_FILES} files per message.` });
    const next = [];
    for (const file of picked.slice(0, room)) {
      const kind = KIND[file.type];
      if (!kind) {
        notification.error({ message: `${file.name} cannot be attached`, description: "Images, mp4 video, PDF, Word, Excel, CSV or text files." });
        continue;
      }
      const key = `f${(seq.current += 1)}`;
      // A picked image shows at once from the browser's own copy; the stored
      // copy is fetched only when the conversation is opened again.
      const preview = kind === "image" ? URL.createObjectURL(file) : null;
      next.push({ key, name: file.name, size: file.size, mime: file.type, kind, status: "uploading", preview });
      uploadSingleFile(file, "copilot-attachment", null)
        .then((r) => setFiles((l) => l.map((f) => (f.key === key ? { ...f, status: "done", file_id: r.fileId } : f))))
        .catch((e) => {
          notification.error({ message: `${file.name} could not be uploaded`, description: e?.response?.data?.message || e.message });
          setFiles((l) => l.filter((f) => f.key !== key));
        });
    }
    count.current += next.length;
    if (next.length) setFiles((cur) => [...cur, ...next]);
  }, []);
  const remove = useCallback((key) => setFiles((l) => l.filter((f) => f.key !== key)), []);
  const clear = useCallback(() => setFiles([]), []);
  const done = files.filter((f) => f.status === "done");
  return {
    files,
    add,
    remove,
    clear,
    uploading: files.some((f) => f.status === "uploading"),
    ids: done.map((f) => f.file_id),
    // What a sent message shows before the stored copy comes back.
    sent: done.map((f) => ({ file_id: f.file_id, name: f.name, mime: f.mime, size: f.size, kind: f.kind, preview: f.preview })),
  };
}

const ClipButton = styled(MicButton)`
  right: ${(p) => p.$right}px;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/** The paperclip in the message box, with its hidden file picker. */
export function AttachButton({ attachments, right = 52, disabled }) {
  const input = useRef(null);
  const full = attachments.files.length >= MAX_FILES;
  return (
    <>
      <Tooltip title={disabled ? "Attach files once Copilot has finished" : full ? `Up to ${MAX_FILES} files per message` : "Attach a brief, a discussion guide, or an image or video for a question"}>
        <ClipButton type="button" $right={right} disabled={disabled || full} aria-label="Attach files" onClick={() => input.current?.click()}>
          <PaperClipOutlined />
        </ClipButton>
      </Tooltip>
      <input ref={input} type="file" accept={ACCEPT} multiple hidden
        onChange={(e) => { attachments.add(e.target.files); e.target.value = ""; }} />
    </>
  );
}

const sizeText = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

const open = async (fileId) => {
  try {
    const r = await generateDownloadUrl(fileId);
    if (r?.data?.downloadUrl) window.open(r.data.downloadUrl, "_blank", "noopener");
  } catch (_) {
    notification.error({ message: "The file could not be opened." });
  }
};

// Signed read links for stored files, kept until shortly before they expire so
// a re-rendered conversation does not ask again for every image.
const urlCache = new Map();
const LINK_SECONDS = 3600;
export function useFileUrl(fileId) {
  const cached = fileId ? urlCache.get(fileId) : null;
  const fresh = cached && cached.until > Date.now() ? cached : null;
  const [url, setUrl] = useState(fresh ? fresh.url : null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!fileId) return undefined;
    const hit = urlCache.get(fileId);
    if (hit && hit.until > Date.now()) { setUrl(hit.url); return undefined; }
    let alive = true;
    const pending = hit && hit.pending ? hit.pending : generateDownloadUrl(fileId, LINK_SECONDS).then((r) => {
      const u = r?.data?.downloadUrl || null;
      if (u) urlCache.set(fileId, { url: u, until: Date.now() + (LINK_SECONDS - 120) * 1000 });
      else urlCache.delete(fileId);
      return u;
    }).catch(() => { urlCache.delete(fileId); return null; });
    if (!(hit && hit.pending)) urlCache.set(fileId, { pending, until: 0 });
    pending.then((u) => { if (!alive) return; if (u) setUrl(u); else setFailed(true); });
    return () => { alive = false; };
  }, [fileId]);
  return { url, failed };
}

const ThumbRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;
const ThumbBox = styled.figure`
  position: relative;
  margin: 0;
  width: ${(p) => p.$w}px;
  flex: none;
  .pic {
    position: relative;
    width: 100%;
    height: ${(p) => Math.round(p.$w * 0.75)}px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${HAIRLINE};
    background: rgba(109, 106, 103, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${FIG_MUTED};
  }
  .pic .ant-image, .pic .ant-image-img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .busy { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.55); color: ${PRIMARY}; }
  .rm {
    all: unset; cursor: pointer; position: absolute; top: 4px; right: 4px; width: 18px; height: 18px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center; font-size: 12px; line-height: 1;
    background: rgba(44, 41, 38, 0.62); color: #fff;
    &:hover { background: rgba(44, 41, 38, 0.82); }
    &:focus-visible { outline: 2px solid ${PRIMARY}; }
  }
  figcaption { font-size: 11px; color: ${FIG_MUTED}; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

/**
 * One image as a thumbnail that opens full size: a stored file by id, or the
 * browser's own copy while it uploads. caption names it under the picture.
 */
export function AttachmentThumb({ fileId, preview, name, caption, width = 96, uploading, onRemove }) {
  const stored = useFileUrl(preview ? null : fileId);
  const src = preview || stored.url;
  return (
    <ThumbBox $w={width} title={name}>
      <div className="pic">
        {src ? (
          <Image src={src} alt={name || "Attached image"} preview={uploading ? false : { mask: <EyeOutlined aria-label="View full size" /> }} />
        ) : stored.failed ? <FileImageOutlined aria-label={`${name || "Image"} could not be shown`} /> : <LoadingOutlined />}
        {uploading ? <span className="busy"><LoadingOutlined /></span> : null}
      </div>
      {onRemove ? <button type="button" className="rm" aria-label={`Remove ${name}`} onClick={onRemove}>×</button> : null}
      {caption ? <figcaption>{caption}</figcaption> : null}
    </ThumbBox>
  );
}

/** Files on a message or in the message box: images as thumbnails, the rest as chips. */
export function AttachmentChips({ files, onRemove, style }) {
  if (!files || !files.length) return null;
  const images = files.filter((f) => f.kind === "image" && (f.preview || f.file_id));
  const others = files.filter((f) => !images.includes(f));
  const align = style && style.justifyContent ? { justifyContent: style.justifyContent } : null;
  return (
    <>
      {images.length ? (
        <ThumbRow style={{ ...(style || {}), ...(others.length ? { marginBottom: 6 } : {}), ...(align || {}) }}>
          {images.map((f) => (
            <AttachmentThumb key={f.key || f.file_id} fileId={f.file_id} preview={f.preview} name={f.name}
              uploading={f.status === "uploading"} onRemove={onRemove ? () => onRemove(f.key) : undefined} />
          ))}
        </ThumbRow>
      ) : null}
      {others.length ? <FileChips files={others} onRemove={onRemove} style={images.length ? { ...(style || {}), marginTop: 0 } : style} /> : null}
    </>
  );
}

function FileChips({ files, onRemove, style }) {
  return (
    <ContextRow style={style}>
      {files.map((f) => {
        const Icon = f.status === "uploading" ? LoadingOutlined : ICON[f.kind] || FileTextOutlined;
        return (
          <ContextChip key={f.key || f.file_id} title={`${f.name} · ${sizeText(f.size)}`} style={onRemove ? undefined : { paddingRight: 10, cursor: "pointer" }}
            onClick={onRemove || !f.file_id ? undefined : () => open(f.file_id)}>
            <Icon />
            <span className="t">{f.name}</span>
            {onRemove ? <button type="button" aria-label={`Remove ${f.name}`} onClick={() => onRemove(f.key)}>×</button> : null}
          </ContextChip>
        );
      })}
    </ContextRow>
  );
}

const Veil = styled.div`
  position: fixed;
  z-index: 1200;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 2px dashed ${(p) => (p.$off ? "rgba(109, 106, 103, 0.5)" : PRIMARY)};
  background: ${(p) => (p.$off ? "rgba(241, 240, 239, 0.88)" : "rgba(238, 240, 252, 0.9)")};
  .in { display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; padding: 16px; }
  .in .ico { font-size: 26px; color: ${(p) => (p.$off ? FIG_MUTED : PRIMARY)}; }
  .in b { font-size: 14px; color: ${FIG_INK}; }
  .in span { font-size: 12px; color: ${FIG_MUTED}; }
`;

/**
 * Drag files onto the conversation, or paste a copied image, to attach them.
 * Placed anywhere inside the area that should take drops: it listens on its
 * parent element. While Copilot is working nothing is attached, and the veil
 * says so instead of letting the browser open the file.
 */
export function FileDropHost({ attachments, disabled }) {
  const anchor = useRef(null);
  const [rect, setRect] = useState(null);
  const addRef = useRef(attachments.add);
  addRef.current = attachments.add;
  const offRef = useRef(disabled);
  offRef.current = disabled;
  useEffect(() => {
    const host = anchor.current && anchor.current.parentElement;
    if (!host) return undefined;
    let depth = 0;
    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes("Files");
    const enter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth += 1;
      const r = host.getBoundingClientRect();
      setRect({ top: r.top + 6, left: r.left + 6, width: Math.max(r.width - 12, 0), height: Math.max(r.height - 12, 0) });
    };
    const over = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = offRef.current ? "none" : "copy";
    };
    const leave = (e) => {
      if (!hasFiles(e)) return;
      depth = Math.max(depth - 1, 0);
      if (!depth) setRect(null);
    };
    const drop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setRect(null);
      if (offRef.current) return;
      addRef.current(e.dataTransfer.files);
    };
    // A screenshot pasted into the message box: files only, no text with it.
    const paste = (e) => {
      const cd = e.clipboardData;
      if (!cd || !cd.files || !cd.files.length || offRef.current) return;
      if (Array.from(cd.types || []).includes("text/plain")) return;
      e.preventDefault();
      addRef.current(cd.files);
    };
    host.addEventListener("dragenter", enter);
    host.addEventListener("dragover", over);
    host.addEventListener("dragleave", leave);
    host.addEventListener("drop", drop);
    host.addEventListener("paste", paste);
    return () => {
      host.removeEventListener("dragenter", enter);
      host.removeEventListener("dragover", over);
      host.removeEventListener("dragleave", leave);
      host.removeEventListener("drop", drop);
      host.removeEventListener("paste", paste);
    };
  }, []);
  return (
    <>
      <span ref={anchor} hidden />
      {rect ? createPortal(
        <Veil $off={disabled} style={rect} aria-hidden="true">
          <div className="in">
            <PaperClipOutlined className="ico" />
            <b>{disabled ? "Attach files once Copilot has finished" : "Drop to attach"}</b>
            {disabled ? null : <span>Images, mp4 video, PDF, Word, Excel, CSV or text. Up to {MAX_FILES} per message.</span>}
          </div>
        </Veil>,
        document.body,
      ) : null}
    </>
  );
}
